import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  CORE_READY_PRINCIPLES,
  FOUNDATION_LAYERS,
  createFoundation,
  deleteFoundation,
  fetchFoundationStats,
  fetchFoundationStatus,
  type FoundationStatus,
} from '@/lib/coreFoundation';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function FoundationTab({ org }: Props) {
  const [foundations, setFoundations] = useState<FoundationStatus[]>([]);
  const [stats, setStats] = useState({ total: 0, healthy: 0, categories: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [form, setForm] = useState({ foundationKey: '', label: '', category: '', sourceTable: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, s] = await Promise.all([fetchFoundationStatus(), fetchFoundationStats()]);
      setFoundations(list);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onAdd = async () => {
    if (!form.foundationKey.trim() || !form.label.trim()) { setError('Clé et libellé requis.'); return; }
    setSaving(true);
    setError('');
    try {
      await createFoundation({
        foundationKey: form.foundationKey.trim(),
        label: form.label.trim(),
        category: form.category || null,
        sourceTable: form.sourceTable || null,
      });
      setForm({ foundationKey: '', label: '', category: '', sourceTable: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f: FoundationStatus) => {
    setBusy(f.id);
    try { await deleteFoundation(f.id); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Objectif final.</span> Zifek Core est la fondation
        permanente de Zifek : tous les futurs modules utilisent ces systèmes sans les recréer. L’interface est
        construite au-dessus de cette architecture — centrale, stable, sécurisée, multi-tenant, API-first et AI-ready.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {/* Architecture cible */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2"><i className="ri-node-tree text-primary-600"></i>Architecture visée</h4>
        <div className="space-y-2">
          {FOUNDATION_LAYERS.map((l, i) => (
            <div key={l.key}>
              <div className={`rounded-lg border p-3 flex items-center gap-3 ${l.role === 'core' ? 'border-primary-200 bg-primary-50' : l.role === 'modules' ? 'border-secondary-200 bg-secondary-50' : 'border-accent-200 bg-accent-50'}`}>
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${l.role === 'core' ? 'bg-primary-100 text-primary-700' : l.role === 'modules' ? 'bg-secondary-100 text-secondary-700' : 'bg-accent-100 text-accent-700'}`}><i className={l.icon}></i></span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-900">{l.label}</p>
                  <p className="text-[11px] text-foreground-500">{l.desc}</p>
                </div>
              </div>
              {i < FOUNDATION_LAYERS.length - 1 && <div className="flex justify-center py-0.5"><i className="ri-arrow-down-line text-foreground-300"></i></div>}
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {CORE_READY_PRINCIPLES.map((p) => (
            <span key={p.key} className="text-[11px] px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 flex items-center gap-1.5"><i className={`${p.icon} text-primary-600`}></i>{p.label}</span>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-stack-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Systèmes fondamentaux</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-heart-pulse-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.healthy}</p>
          <p className="text-xs text-foreground-500">En état (vérifiés en base)</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-grid-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.categories}</p>
          <p className="text-xs text-foreground-500">Catégories</p>
        </div>
      </div>

      {/* Registre */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-stack-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Systèmes fondamentaux réutilisables</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 divide-background-200/60">
            {foundations.map((f) => (
              <div key={f.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-background-200/60">
                <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0"><i className={f.icon || 'ri-stack-line'}></i></span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-foreground-800 flex items-center gap-2">
                    {f.label}
                    {f.healthy ? <span className="w-1.5 h-1.5 rounded-full bg-primary-500" title="Vérifié en base"></span> : <span className="w-1.5 h-1.5 rounded-full bg-foreground-300" title="Table non vérifiable"></span>}
                  </p>
                  <p className="text-[11px] text-foreground-400 truncate">{f.category} · {f.source_table}{f.liveCount != null ? ` · ${f.liveCount} ligne(s)` : ''}</p>
                </div>
                <button onClick={() => remove(f)} disabled={busy === f.id} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-40"><i className="ri-delete-bin-line text-sm"></i></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-add-line text-accent-600"></i>Déclarer un système fondamental</h4>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input className={INPUT} placeholder="Clé (ex : subscriptions)" value={form.foundationKey} onChange={(e) => setForm({ ...form, foundationKey: e.target.value })} />
          <input className={INPUT} placeholder="Libellé" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <input className={INPUT} placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <input className={INPUT} placeholder="Table source" value={form.sourceTable} onChange={(e) => setForm({ ...form, sourceTable: e.target.value })} />
        </div>
        <button onClick={onAdd} disabled={saving} className="mt-3 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Déclarer
        </button>
      </div>
    </div>
  );
}